import { Injectable } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { select, Store } from '@ngrx/store'
import { filter } from 'rxjs/operators'
import { AppState } from '../core/store'
import { selectUserName } from '../core/store/auth/auth.selectors'
import { flattenAllNavItems, HomeShortcut } from '../nav-items.util'
import { NAV_ITEMS } from '../nav-items.const'

export interface RecentSection {
  displayName: string
  iconName: string
  route: string
  accentClass: string
  contextLabel: string | null
}

const MAX_RECENTS = 5

@Injectable({ providedIn: 'root' })
export class RecentSectionsService {
  private allShortcuts: HomeShortcut[] = flattenAllNavItems(NAV_ITEMS)
  private username: string | null = null

  constructor(
    private router: Router,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(selectUserName)).subscribe((username) => {
      this.username = username
    })

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.recordVisit(event.urlAfterRedirects))
  }

  getRecents(): RecentSection[] {
    const key = this.storageKey
    return key ? this.readRecents(key) : []
  }

  private get storageKey(): string | null {
    return this.username ? `home.recentSections.${this.username}` : null
  }

  private recordVisit(url: string): void {
    // Credit routes are frequently visited with a client/year/program prefix
    // (e.g. /clients/106/years/260/programs/988/credit/mi/design), so a
    // shortcut's bare route (/credit/mi/design) is matched as a path segment
    // anywhere in the url, not just as a leading prefix.
    const match = this.allShortcuts.find((shortcut) => this.matchesRoute(url, shortcut.route))
    const key = this.storageKey
    if (!match || !key) {
      return
    }

    const existing = this.readRecents(key).filter((recent) => recent.route !== match.route)
    const updated: RecentSection[] = [
      {
        displayName: match.displayName,
        iconName: match.iconName,
        route: match.route,
        accentClass: match.accentClass,
        contextLabel: match.contextLabel,
      },
      ...existing,
    ].slice(0, MAX_RECENTS)

    localStorage.setItem(key, JSON.stringify(updated))
  }

  // True when `route` (e.g. "/credit/mi/design") appears in `url` at a path
  // segment boundary — either as a leading prefix or embedded after a
  // client/year/program prefix (e.g. "/clients/106/.../credit/mi/design" or
  // ".../credit/mi/design/portfolio/abc/structure/def/tower").
  private matchesRoute(url: string, route: string): boolean {
    const index = url.indexOf(route)
    if (index === -1) {
      return false
    }
    const charAfter = url[index + route.length]
    return charAfter === undefined || charAfter === '/'
  }

  private readRecents(key: string): RecentSection[] {
    try {
      const raw = localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as RecentSection[]) : []
    } catch {
      return []
    }
  }
}
